import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  type RouteComponent,
} from '@tanstack/react-router';
import { LoginPage } from './features/auth/LoginPage.js';
import { HomePage } from './features/home/HomePage.js';
import { NotFoundPage } from './features/not-found/NotFoundPage.js';
import { AppShell } from './layouts/AppShell.js';
import { registeredRoutes } from './lib/nav.js';

// Route-level code splitting (PLAN §3.6: < 200 KB gzip for the first route). Sign-in and Today load
// eagerly; everything else is fetched when first visited (and preloaded on link hover).
const lazy = <M extends Record<string, unknown>>(
  load: () => Promise<M>,
  name: keyof M & string,
): RouteComponent => lazyRouteComponent(load, name);

const rootRoute = createRootRoute({ component: Outlet, notFoundComponent: NotFoundPage });

const passwordPages = () => import('./features/auth/PasswordPages.js');

// Signed-out screens
const login = createRoute({ getParentRoute: () => rootRoute, path: '/login', component: LoginPage });
const forgot = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: lazy(passwordPages, 'ForgotPasswordPage'),
});
const reset = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: lazy(passwordPages, 'ResetPasswordPage'),
});
const accept = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invite',
  component: lazy(passwordPages, 'AcceptInvitePage'),
});

// Public privacy notice (D-127): Meta and Google lead-form apps link here.
const privacy = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: lazy(() => import('./features/auth/PrivacyPage.js'), 'PrivacyPage'),
});

// Signed-in app
const shell = createRoute({ getParentRoute: () => rootRoute, id: 'app', component: AppShell });
const page = (path: string, component: RouteComponent) => {
  registeredRoutes.add(path);
  return createRoute({ getParentRoute: () => shell, path, component });
};

const auditSettings = () => import('./features/admin/AuditSettingsPages.js');
const teamsRoles = () => import('./features/admin/TeamsRolesPages.js');
const modulePage = lazy(() => import('./extensions/ModulePage.js'), 'ModulePage');

const routeTree = rootRoute.addChildren([
  login,
  forgot,
  reset,
  accept,
  privacy,
  shell.addChildren([
    page('/', HomePage),
    page(
      '/profile',
      lazy(() => import('./features/profile/ProfilePage.js'), 'ProfilePage'),
    ),
    // Leads: one page with list, board and visits tabs; each lead on its own page.
    page(
      '/leads',
      lazy(() => import('./features/leads/LeadsHubPage.js'), 'LeadsHubPage'),
    ),
    createRoute({
      getParentRoute: () => shell,
      path: '/leads/$leadId',
      component: lazy(() => import('./features/leads/LeadPage.js'), 'LeadPage'),
    }),
    page(
      '/leads/import',
      lazy(() => import('./features/leads/ImportLeadsPage.js'), 'ImportLeadsPage'),
    ),
    page(
      '/pipeline',
      lazy(() => import('./features/leads/LeadsHubPage.js'), 'PipelineRedirect'),
    ),
    page(
      '/pipeline/drop-off',
      lazy(() => import('./features/leads/DropOffPage.js'), 'DropOffPage'),
    ),
    page(
      '/walk-in',
      lazy(() => import('./features/leads/WalkInPage.js'), 'WalkInPage'),
    ),
    page(
      '/visits',
      lazy(() => import('./features/leads/LeadsHubPage.js'), 'VisitsRedirect'),
    ),
    page(
      '/admin/leads',
      lazy(() => import('./features/leads/LeadSettingsPage.js'), 'LeadSettingsPage'),
    ),
    // Comms (BUILD_BRIEF §9.4, M16)
    page(
      '/inbox',
      lazy(() => import('./features/comms/InboxPage.js'), 'InboxPage'),
    ),
    page(
      '/calls',
      lazy(() => import('./features/comms/CallsPage.js'), 'CallsPage'),
    ),
    page(
      '/training-library',
      lazy(() => import('./features/comms/TrainingLibraryPage.js'), 'TrainingLibraryPage'),
    ),
    page(
      '/reports/comms',
      lazy(() => import('./features/comms/CommsReportsPage.js'), 'CommsReportsPage'),
    ),
    page(
      '/admin/integrations',
      lazy(() => import('./features/comms/IntegrationsPage.js'), 'IntegrationsPage'),
    ),
    // Insights (M12, D-137, D-138)
    page(
      '/insights',
      lazy(() => import('./features/insights/TrendsPage.js'), 'TrendsPage'),
    ),
    page(
      '/insights/reports',
      lazy(() => import('./features/insights/ReportsPage.js'), 'ReportsPage'),
    ),
    // Marketing (M9, D-135)
    page(
      '/marketing',
      lazy(() => import('./features/marketing/MarketingPages.js'), 'AttributionPage'),
    ),
    page(
      '/marketing/sources',
      lazy(() => import('./features/marketing/MarketingPages.js'), 'SourceScorecardPage'),
    ),
    page(
      '/marketing/ad-accounts',
      lazy(() => import('./features/marketing/MarketingPages.js'), 'AdAccountsPage'),
    ),
    page(
      '/marketing/campaigns',
      lazy(() => import('./features/marketing/CampaignsPages.js'), 'CampaignsPage'),
    ),
    createRoute({
      getParentRoute: () => shell,
      path: '/marketing/campaigns/$id',
      component: lazy(() => import('./features/marketing/CampaignsPages.js'), 'CampaignPage'),
    }),
    page(
      '/marketing/settings',
      lazy(() => import('./features/marketing/CampaignsPages.js'), 'MarketingSettingsPage'),
    ),
    // Inventory (M3)
    page(
      '/projects',
      lazy(() => import('./features/inventory/ProjectsPage.js'), 'ProjectsPage'),
    ),
    page(
      '/inventory',
      lazy(() => import('./features/inventory/InventoryPage.js'), 'InventoryPage'),
    ),
    page(
      '/site-plans',
      lazy(() => import('./features/siteplan/SitePlansPage.js'), 'SitePlansPage'),
    ),
    createRoute({
      getParentRoute: () => shell,
      path: '/site-plans/$id',
      component: lazy(() => import('./features/siteplan/SitePlanPage.js'), 'SitePlanPage'),
    }),
    page(
      '/approvals',
      lazy(() => import('./features/inventory/ApprovalsPage.js'), 'ApprovalsPage'),
    ),
    createRoute({
      getParentRoute: () => shell,
      path: '/projects/$id',
      component: lazy(() => import('./features/inventory/ProjectPage.js'), 'ProjectPage'),
    }),
    // Post-sales (M13)
    page(
      '/bookings',
      lazy(() => import('./features/postsales/BookingsPage.js'), 'BookingsPage'),
    ),
    createRoute({
      getParentRoute: () => shell,
      path: '/bookings/$id',
      component: lazy(() => import('./features/postsales/BookingPage.js'), 'BookingPage'),
    }),
    page(
      '/receivables',
      lazy(() => import('./features/postsales/ReceivablesPage.js'), 'ReceivablesPage'),
    ),
    page(
      '/booking-approvals',
      lazy(() => import('./features/postsales/BookingApprovalsPage.js'), 'BookingApprovalsPage'),
    ),
    page(
      '/construction-updates',
      lazy(() => import('./features/postsales/ConstructionUpdatesPage.js'), 'ConstructionUpdatesPage'),
    ),
    // Channel partners (M14)
    page(
      '/partners',
      lazy(() => import('./features/partners/PartnersPage.js'), 'PartnersPage'),
    ),
    page(
      '/partners/commission-plans',
      lazy(() => import('./features/partners/CommissionPlansPage.js'), 'CommissionPlansPage'),
    ),
    page(
      '/partners/payouts',
      lazy(() => import('./features/partners/PayoutQueuePage.js'), 'PayoutQueuePage'),
    ),
    page(
      '/partners/broadcasts',
      lazy(() => import('./features/partners/BroadcastsPage.js'), 'BroadcastsPage'),
    ),
    page(
      '/admin/payment-gateway',
      lazy(() => import('./features/payments/PaymentGatewayPage.js'), 'PaymentGatewayPage'),
    ),
    page(
      '/admin/post-sales',
      lazy(() => import('./features/postsales/PostSalesSettingsPage.js'), 'PostSalesSettingsPage'),
    ),
    page(
      '/admin/members',
      lazy(() => import('./features/admin/MembersPage.js'), 'MembersPage'),
    ),
    // Team operations (M10)
    page(
      '/targets',
      lazy(() => import('./features/team/TargetsPage.js'), 'TargetsPage'),
    ),
    page(
      '/leaderboard',
      lazy(() => import('./features/team/LeaderboardPage.js'), 'LeaderboardPage'),
    ),
    page(
      '/incentives',
      lazy(() => import('./features/team/IncentivesPage.js'), 'IncentivesPage'),
    ),
    page(
      '/attendance',
      lazy(() => import('./features/team/AttendancePage.js'), 'AttendancePage'),
    ),
    page(
      '/admin/handover',
      lazy(() => import('./features/team/HandoverPage.js'), 'HandoverPage'),
    ),
    page('/admin/teams', lazy(teamsRoles, 'TeamsPage')),
    page('/admin/roles', lazy(teamsRoles, 'RolesPage')),
    page('/admin/audit', lazy(auditSettings, 'AuditPage')),
    page(
      '/admin/exports',
      lazy(() => import('./features/admin/ExportLogPage.js'), 'ExportLogPage'),
    ),
    page(
      '/admin',
      lazy(() => import('./features/admin/SettingsHomePage.js'), 'SettingsHomePage'),
    ),
    page('/admin/settings', lazy(auditSettings, 'SettingsPage')),
    // DPDP requests and the organisation export (D-142)
    page(
      '/admin/privacy',
      lazy(() => import('./features/privacy/PrivacyPage.js'), 'PrivacyPage'),
    ),
    // M16 configuration without code (D-141)
    page(
      '/admin/customise',
      lazy(() => import('./features/customisation/CustomisePage.js'), 'CustomisePage'),
    ),
    page(
      '/setup',
      lazy(() => import('./features/onboarding/SetupPage.js'), 'SetupPage'),
    ),
    page(
      '/admin/sso',
      lazy(() => import('./features/admin/SsoPage.js'), 'SsoPage'),
    ),
    page(
      '/admin/events',
      lazy(() => import('./features/admin/EventsPage.js'), 'EventsPage'),
    ),
    page(
      '/admin/modules',
      lazy(() => import('./features/admin/ModulesPage.js'), 'ModulesPage'),
    ),
    // Extension module pages (routes resolved inside ModulePage).
    createRoute({ getParentRoute: () => shell, path: '/m/$module', component: modulePage }),
    createRoute({ getParentRoute: () => shell, path: '/m/$module/$', component: modulePage }),
  ]),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

// Routes are registered dynamically (features and extension modules), so the router is not
// type-registered; links use plain string paths.
